import React, { useMemo, useState, useCallback, useEffect, } from 'react';
import { keyBy, isEmpty, } from 'lodash';
import { startOfDay, endOfDay, format as formatDate, subDays, } from 'date-fns';
import numeral from 'numeral';

import { getCollectionData, } from '../../shared/firebase';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import Page from '../hocs/Page';
import QuerySelector from '../QuerySelector';
import DateSelector from '../DateSelector';
import CursorPagination from '../CursorPagination';
import ExportButton from '../ExportButton';
import { mallOrderSkipReasons } from '../../shared/config';
import { priceTaxTypes, } from '../../shared/models/product';

const countPerPage = 100;

const mallOptions = [
  { label: '楽天', value: 'rakuten' },
  { label: 'Shopify', value: 'shopify' },
  { label: 'Yahoo', value: 'yahoo' },
  { label: 'TikTok Shop', value: 'tts' },
  { label: 'メルカリ', value: 'mercari' },
  { label: 'Qoo10', value: 'qoo10' },
];

const statusOptions = [
  { label: '未処理', value: 'unprocessed' },
  { label: '処理済み', value: 'processed' },
];

const statusLabels = {
  unprocessed: '未処理',
  processed: '処理済み',
};

const mallLabels = {
  rakuten: '楽天',
  shopify: 'Shopify',
  yahoo: 'Yahoo',
  tts: 'TikTok Shop',
  mercari: 'メルカリ',
  qoo10: 'Qoo10',
};

export default Page(function MallOrders (props) {
  const { tenant } = props;
  const queryParams = useQueryParams();
  const {
    startOn: startOnString,
    endOn: endOnString,
    shopId: shopIdFilter = [],
    status: statusFilter = [],
    mall: mallFilter = [],
  } = queryParams;
  const defaultStartOn = subDays(new Date(), 30);
  const startOn = !isEmpty(startOnString) ? new Date(startOnString) : defaultStartOn;
  const endOn = !isEmpty(endOnString) ? new Date(endOnString) : new Date();

  const [cursors, setCursors] = useState([]);
  const currentCursor = cursors[cursors.length - 1] || null;

  const shops = useCollectionSubscription(tenant.ref.collection('shops').orderBy('createdAt'), [tenant.id]);
  const shopsById = keyBy(shops, 'id');
  const shopOptions = shops.map(_ => ({ label: _.name, value: _.id }));

  const shopIdValue = shopIdFilter[0];
  const statusValue = statusFilter[0];
  const mallValue = mallFilter[0];

  useEffect(() => setCursors([]), [startOnString, endOnString, shopIdValue, statusValue, mallValue]);

  const filteredMallOrdersRef = useMemo(() => {
    let ref = tenant.ref.collection('mallOrders')
      .where('orderedAt', '>=', startOfDay(startOn))
      .where('orderedAt', '<=', endOfDay(endOn))
      .orderBy('orderedAt', 'desc');
    if (shopIdValue) ref = ref.where('shopId', '==', shopIdValue);
    if (statusValue) ref = ref.where('status', '==', statusValue);
    if (mallValue) ref = ref.where('mall', '==', mallValue);
    return ref;
  }, [tenant.id, startOnString, endOnString, shopIdValue, statusValue, mallValue]);
  const mallOrdersRef = useMemo(() => filteredMallOrdersRef.limit(countPerPage + 1), [filteredMallOrdersRef]);
  const mallOrdersRefWithCursor = useMemo(() => {
    return currentCursor ? mallOrdersRef.startAfter(currentCursor) : mallOrdersRef;
  }, [mallOrdersRef, currentCursor]);
  const { items: rawMallOrders, isLoading } = useCollectionSubscription(mallOrdersRefWithCursor, [tenant.id, startOnString, endOnString, shopIdValue, statusValue, mallValue, currentCursor], { detail: true, initialItems: null });

  const hasNext = (rawMallOrders?.length || 0) > countPerPage;
  const mallOrders = hasNext ? rawMallOrders.slice(0, countPerPage) : (rawMallOrders || []);

  const hasPrev = cursors.length > 0;
  const onNext = useCallback(() => {
    if (!hasNext || mallOrders.length === 0) return;
    setCursors(_ => [..._, mallOrders[mallOrders.length - 1].orderedAt]);
  }, [hasNext, mallOrders]);
  const onPrev = useCallback(() => setCursors(_ => _.slice(0, -1)), []);
  const onFirst = useCallback(() => setCursors([]), []);

  const rowsForExport = async () => {
    const allMallOrders = await getCollectionData(filteredMallOrdersRef);
    return allMallOrders.flatMap((mallOrder) => {
      const { orderNumber, mall, shopId, customerName, orderedAt, status, shippingRequestId, skipReason, items = [], } = mallOrder;
      const orderRow = {
        注文番号: orderNumber,
        モール: mallLabels[mall] || mall,
        店舗: shopsById[shopId]?.name || shopId,
        注文者名: customerName,
        注文日時: orderedAt?.toDate && formatDate(orderedAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
      };
      const statusRow = {
        ステータス: statusLabels[status] || status,
        出荷依頼ID: shippingRequestId && `${tenant.id}-${shippingRequestId}`,
        未作成理由: !shippingRequestId && skipReason ? (mallOrderSkipReasons[skipReason] || skipReason) : null,
      };
      return (isEmpty(items) ? [{}] : items).map(_ => ({
        ...orderRow,
        商品コード: _.code,
        商品名: _.name,
        数量: _.quantity,
        単価: _.unitPrice,
        税区分: priceTaxTypes[_.priceTaxType]?.label,
        ...statusRow,
      }));
    });
  };

  return (
    <div className="mall-orders container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-start align-items-end mb-2 gap-1">
          <DateSelector paramName="startOn" label="開始日" defaultValue={defaultStartOn} />
          <DateSelector paramName="endOn" label="終了日" defaultValue={new Date()} />
          <QuerySelector width={200} paramName="mall" options={mallOptions} label="モール" />
          <QuerySelector width={200} paramName="shopId" options={shopOptions} label="店舗" />
          <QuerySelector width={200} paramName="status" options={statusOptions} label="ステータス" />
        </div>
        <div className="d-flex justify-content-end align-items-center mb-1 gap-1">
          <ExportButton label="CSV出力" fileName={`受注_${formatDate(startOn, 'yyyyMMdd')}-${formatDate(endOn, 'yyyyMMdd')}.csv`} rows={rowsForExport} />
          <CursorPagination page={cursors.length + 1} hasPrev={hasPrev} hasNext={hasNext} onFirst={onFirst} onPrev={onPrev} onNext={onNext} />
        </div>
        <div className="overflow-auto position-relative" style={{ zIndex: 0 }}>
          {
            mallOrders.length > 0 || isLoading ? (
              <table className="table table-sm table-bordered sticky-table">
                <thead className="thead-light text-center">
                  <tr>
                    <th>注文番号</th>
                    <th>モール</th>
                    <th>店舗</th>
                    <th>注文者名</th>
                    <th>注文日時</th>
                    <th style={{ minWidth: 300 }}>商品・販売価格</th>
                    <th>ステータス</th>
                    <th>サンクスメール</th>
                    <th>出荷依頼</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    mallOrders.map((mallOrder) => {
                      const { id, orderNumber, mall, shopId, customerName, orderedAt, status, shippingRequestId, skipReason, thanksMailSentAt, thanksMailFailedAt, items = [], } = mallOrder;
                      return (
                        <tr key={id}>
                          <td>{orderNumber}</td>
                          <td>{mallLabels[mall] || mall}</td>
                          <td>{shopsById[shopId]?.name || shopId}</td>
                          <td>{customerName}</td>
                          <td>{orderedAt?.toDate && formatDate(orderedAt.toDate(), 'yyyy-MM-dd HH:mm')}</td>
                          <td>
                            {
                              items.map((item, i) => (
                                <div key={i} className="d-flex justify-content-between gap-1 small">
                                  <span>{item.name || item.code} × {item.quantity}</span>
                                  <span className="text-nowrap">
                                    {item.unitPrice != null && `${numeral(item.unitPrice).format('0,0')}円（${priceTaxTypes[item.priceTaxType]?.label}）`}
                                  </span>
                                </div>
                              ))
                            }
                          </td>
                          <td>
                            <span className={`badge badge-${status === 'processed' ? 'success' : 'warning'}`}>
                              {statusLabels[status] || status}
                            </span>
                          </td>
                          <td>
                            {thanksMailSentAt?.toDate && formatDate(thanksMailSentAt.toDate(), 'yyyy-MM-dd HH:mm')}
                            {thanksMailFailedAt && !thanksMailSentAt && <span className="text-danger">失敗</span>}
                          </td>
                          <td>
                            {shippingRequestId && `${tenant.id}-${shippingRequestId}`}
                            {!shippingRequestId && skipReason && <small className="text-danger">未作成：{mallOrderSkipReasons[skipReason] || skipReason}</small>}
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                {
                  isLoading ? (
                    <span className="fas fa-spin fa-spinner" />
                  ) : (
                    <span>受注はありません</span>
                  )
                }
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});
